import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle, ChevronRight, Truck } from "lucide-react";
import api from "@/lib/api";
import { expiryStatus, severityRank, formatDaysLabel } from "@/lib/expiry";

// Compliance row for a single vehicle/check (one row per WoF/Rego that needs attention).
function AlertRow({ vehicle, kind, status }) {
  const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };
  return (
    <Link
      to="/app/fleet"
      className="flex items-center gap-3 px-3 py-2.5 border-b border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-[#151C27] last:border-b-0 transition-colors"
      data-testid={`compliance-alert-${vehicle.id}-${kind.toLowerCase()}`}
    >
      <Truck className="w-4 h-4 text-zinc-400 dark:text-zinc-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className="text-[13px] tabular-nums text-zinc-900 dark:text-white truncate"
            style={{ ...fontHead, fontWeight: 800, letterSpacing: "0.04em" }}
          >
            {vehicle.rego}
          </span>
          <span className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 dark:text-zinc-400 truncate" style={{ ...fontHead, fontWeight: 700 }}>
            · {vehicle.make} {vehicle.model}
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 dark:text-zinc-400 mt-0.5" style={{ ...fontHead, fontWeight: 700 }}>
          {kind} · {vehicle[kind === "WoF" ? "wof_expiry" : "rego_expiry"]}
        </div>
      </div>
      <span
        className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] border rounded-sm whitespace-nowrap ${status.badge}`}
        style={fontHead}
      >
        {status.severity === "overdue" ? formatDaysLabel(status.days) : status.label}
      </span>
      <span
        className="text-[9px] uppercase tracking-[0.16em] px-2 py-1 rounded-sm bg-zinc-100 dark:bg-[#151C27] text-zinc-700 dark:text-zinc-200 shrink-0"
        style={{ ...fontHead, fontWeight: 700 }}
      >
        Fix →
      </span>
    </Link>
  );
}

export default function FleetComplianceAlerts() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/fleet")
      .then((r) => mounted && setVehicles(r.data || []))
      .catch(() => mounted && setVehicles([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Build flat list of (vehicle, kind, status) for every WoF or Rego in {overdue, 7d, 14d, 30d}.
  const alerts = [];
  for (const v of vehicles) {
    if (v.status === "retired") continue;
    if (v.rego_on_hold) continue; // rego on hold — off the road, reminders muted
    const wof = expiryStatus(v.wof_expiry);
    const rego = expiryStatus(v.rego_expiry);
    if (wof.severity !== "ok" && wof.severity !== "none") {
      alerts.push({ vehicle: v, kind: "WoF", status: wof });
    }
    if (rego.severity !== "ok" && rego.severity !== "none") {
      alerts.push({ vehicle: v, kind: "Rego", status: rego });
    }
  }
  alerts.sort((a, b) => {
    const r = severityRank(a.status.severity) - severityRank(b.status.severity);
    if (r !== 0) return r;
    return (a.status.days ?? 0) - (b.status.days ?? 0);
  });

  const counts = alerts.reduce(
    (acc, a) => ({ ...acc, [a.status.severity]: (acc[a.status.severity] || 0) + 1 }),
    {}
  );

  const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };

  if (loading) {
    return (
      <div className="rounded-sm border border-zinc-300 dark:border-zinc-800 bg-white dark:bg-[#101620] p-5" data-testid="compliance-loading">
        <div className="h-4 w-40 bg-zinc-100 dark:bg-[#151C27] animate-pulse" />
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="rounded-sm border border-zinc-300 dark:border-zinc-800 bg-white dark:bg-[#101620] p-5" data-testid="compliance-all-clear">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center rounded-sm">
            <ShieldCheck className="w-5 h-5 text-emerald-700 dark:text-emerald-400" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-400" style={{ ...fontHead, fontWeight: 700 }}>
              // Fleet compliance · all clear
            </div>
            <div
              className="text-[15px] uppercase tracking-[0.04em] mt-0.5 text-zinc-900 dark:text-white"
              style={{ ...fontHead, fontWeight: 800 }}
            >
              All vehicles current
            </div>
            <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">No WoF or rego renewals due in the next 30 days.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-sm border border-zinc-300 dark:border-zinc-800 bg-white dark:bg-[#101620] overflow-hidden"
      data-testid="compliance-alerts"
    >
      <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-[#151C27] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <div
            className="text-[10px] uppercase tracking-[0.22em] text-amber-500"
            style={{ ...fontHead, fontWeight: 700 }}
          >
            // Fleet · {alerts.length} to action
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {counts.overdue > 0 && (
            <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 bg-red-600 text-white rounded-sm" style={fontHead}>
              {counts.overdue} overdue
            </span>
          )}
          {counts["7d"] > 0 && (
            <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 bg-orange-500 text-white rounded-sm" style={fontHead}>
              {counts["7d"]} ≤7d
            </span>
          )}
          {counts["14d"] > 0 && (
            <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 bg-amber-400 text-zinc-900 rounded-sm" style={fontHead}>
              {counts["14d"]} ≤14d
            </span>
          )}
          {counts["30d"] > 0 && (
            <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 bg-yellow-300 text-zinc-900 rounded-sm" style={fontHead}>
              {counts["30d"]} ≤30d
            </span>
          )}
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {alerts.slice(0, 10).map((a, i) => (
          <AlertRow key={`${a.vehicle.id}-${a.kind}-${i}`} {...a} />
        ))}
      </div>
      {alerts.length > 10 && (
        <Link
          to="/app/fleet"
          className="block px-5 py-2.5 text-center text-[10px] uppercase tracking-[0.18em] text-[#FF5F15] hover:bg-zinc-50 dark:hover:bg-[#151C27] border-t border-zinc-200 dark:border-zinc-800"
          style={{ ...fontHead, fontWeight: 700 }}
          data-testid="compliance-view-all"
        >
          View all {alerts.length} in Fleet →
        </Link>
      )}
      <div className="px-5 py-2 bg-zinc-50 dark:bg-[#151C27] border-t border-zinc-200 dark:border-zinc-800 text-[10px] text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5" style={fontHead}>
        <Truck className="w-3 h-3" />
        Always renew rego via the official NZTA portal — never trust links in emails.
      </div>
    </div>
  );
}
